import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import CategoryEntries from '@/pages/home/components/CategoryEntries';
import VideoSection from '@/pages/home/components/VideoSection';
import { getHotVideos, getLatestVideos } from '@/lib/videoData';
import { useSeo } from '@/hooks/useSeo';

const STEP = 5;

export default function Home() {
  useSeo({
    title: '桃子视频 - 多元化高清视频播放平台',
    description:
      '桃子视频提供丰富多样的视频内容，包括电影、电视剧、综艺等多种类别，用户可以轻松浏览和发现感兴趣的热门视频。',
    keywords: '桃子视频,视频播放,高清视频,影视内容,电影,电视剧,综艺,在线视频',
  });

  const latestAll = useMemo(() => getLatestVideos(10), []);
  const hotAll = useMemo(() => getHotVideos(10), []);
  const [latestCount, setLatestCount] = useState(STEP);
  const [hotCount, setHotCount] = useState(STEP);

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main>
        <h1 className="sr-only">桃子视频 - 多元化高清视频播放平台</h1>
        <HeroCarousel />

        <CategoryEntries />

        <section className="mx-auto max-w-7xl px-4 pt-12 md:px-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div>
              <VideoSection
                icon="ri-time-line"
                title="最新上线"
                subtitle="新片新剧，抢先看"
                videos={latestAll.slice(0, latestCount)}
                moreLink="/category/movie"
              />
              {latestCount < latestAll.length && (
                <button
                  type="button"
                  onClick={() => setLatestCount((v) => Math.min(v + STEP, latestAll.length))}
                  className="mt-4 w-full rounded-md border border-background-700 py-2.5 text-sm font-medium text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-400"
                >
                  加载更多
                </button>
              )}
            </div>

            <div>
              <VideoSection
                icon="ri-fire-line"
                title="热门视频"
                subtitle="大家都在看"
                videos={hotAll.slice(0, hotCount)}
                moreLink="/category/tv"
                showRank
              />
              {hotCount < hotAll.length && (
                <button
                  type="button"
                  onClick={() => setHotCount((v) => Math.min(v + STEP, hotAll.length))}
                  className="mt-4 w-full rounded-md border border-background-700 py-2.5 text-sm font-medium text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-400"
                >
                  加载更多
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-8">
          <div className="relative overflow-hidden rounded-xl border border-primary-900/60 bg-gradient-to-br from-primary-900/40 via-background-900 to-background-900 p-6 md:p-10">
            <span className="pointer-events-none absolute -right-10 -top-10 flex h-40 w-40 items-center justify-center text-primary-500/20">
              <i className="ri-movie-2-line text-[160px]" />
            </span>
            <div className="relative max-w-3xl">
              <h2 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
                关于桃子视频
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-300 md:text-base">
                桃子视频专注于为用户提供丰富、多元的高清视频内容。我们汇聚电影、电视剧、综艺等多类别影视资源，
                以清晰的分类、便捷的筛选和贴心的推荐，帮助每一位观众快速找到心仪的作品。
                无论你是资深影迷还是休闲观众，都能在这里发现属于自己的精彩。
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/category/movie"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-list-2-line" />
                  开始探索片库
                </Link>
                <Link
                  to="/about"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 text-sm font-medium text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
                >
                  了解我们
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}